import React, { useEffect, useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import dayjs from 'dayjs';
import Page from '../../atoms/Page';
import './FaultCreatorPage.scss';

// START FORM STUFF
import { ComboboxField } from '../../atoms/Combobox';
import { gql, useMutation, useQuery } from '@apollo/client';
import { Formik, Form, FieldArray } from 'formik';
import _ from 'lodash';
import useUser from '../../utils/hooks/useUser';
import { ComboboxMultiField } from '../../atoms/ComboboxMulti';
import FeedFloButton from '../../atoms/FeedFloButton';
import { ToggleSwitchField } from '../../atoms/ToggleSwitch';
import { NumberInputField } from '../../atoms/NumberInput';
import LabelledColumn from '../../atoms/LabelledColumn';
import CopyButton from '../../atoms/CopyButton';

const TEST_FAULT_TAG = 'fault_creator_test';

const encodeFaultDetails = (detailsObject) => {
  return btoa(JSON.stringify(detailsObject));
};

const getBinControllerBinOptions = (binController) =>
  (binController?.bin_controller_bins || [])
    .filter((bcb) => bcb.index !== null && bcb.index !== undefined && (bcb.bin?.id || bcb.bin_id))
    .map((bcb) => ({
      id: bcb.bin?.id ?? bcb.bin_id,
      name: bcb.bin?.name ? `${bcb.bin.name} (index ${bcb.index})` : `Bin ${bcb.index}`,
      binIndex: bcb.index,
      binId: bcb.bin?.id ?? bcb.bin_id,
    }));

const buildBinSwitchDetails = (formObject, binController) => {
  if (!formObject?.switchedToBinId) {
    return null;
  }

  const selectedBin = getBinControllerBinOptions(binController).find((option) => option.id === formObject.switchedToBinId);
  if (!selectedBin) {
    return null;
  }

  return encodeFaultDetails({
    binIndex: selectedBin.binIndex,
    bin: selectedBin.binId,
  });
};

// Only Feedflo users
const CONTEXT_GQL = gql`
  query CreateFaultForm_formContextData($org_id_list: [uuid!]!, $before: bigint = "", $after: bigint = "") {
    fault_root_cause(where: { deleted_at: { _is_null: true } }) {
      id
      name
    }
    users: user(
      where: {
        deleted_at: { _is_null: true }
        user_organizations: { _and: { deleted_at: { _is_null: true } }, organization_id: { _in: $org_id_list } }
      }
    ) {
      id
      name
      email
    }
    barns: farm(
      where: {
        deleted_at: { _is_null: true }
        organization: { _and: { deleted_at: { _is_null: true } }, id: { _in: $org_id_list } }
      }
    ) {
      id
      name
      feed_lines(where: { deleted_at: { _is_null: true } }) {
        id
        name
        device_assignments(
          where: {
            deleted_at: { _is_null: true }
            started_at: { _lt: $before }
            _or: [{ ended_at: { _gte: $after } }, { ended_at: { _is_null: true } }]
            status: { _eq: "active" }
            device: { deleted_at: { _is_null: true } }
          }
        ) {
          device {
            id
            serial
            algorithm_version
          }
        }
      }
      bin_sets(where: { deleted_at: { _is_null: true } }) {
        id
        bin_controllers(where: { deleted_at: { _is_null: true }, device: { deleted_at: { _is_null: true } } }) {
          id
          device {
            id
            serial
            algorithm_version
          }
          bin_controller_bins(where: { deleted_at: { _is_null: true } }, order_by: { index: asc }) {
            id
            bin_id
            index
            bin {
              id
              name
            }
          }
        }
      }
    }
  }
`;

const createSimpleFaultConfig = (startedAtMinutes = 60) => ({
  enable: false,
  startedAtMinutes,
  msgSentAtMinutes: 30,
  msgEnable: false,
  rootCause: 'none',
});

const INSERT_FAULT_GQL = gql`
  mutation FaultCreator_insert_fault($faults: [fault_insert_input!]!) {
    __typename
    insert_fault(objects: $faults) {
      affected_rows
    }
  }
`;

const CLEAN_UP_TEST_FAULTS = gql`
  mutation FaultCreator_cleanup_test_faults($device_ids: [uuid!]!, $deleted_at: bigint = "") {
    update_fault(
      where: {
        device_id: { _in: $device_ids }
        fault_comments: { comment: { _eq: "fault_creator_test" } }
        deleted_at: { _is_null: true }
      }
      _set: { deleted_at: $deleted_at }
    ) {
      returning {
        id
      }
    }
  }
`;

const END_FAULT = gql`
  mutation FaultCreator_endFault($id: uuid = "", $ended_at: bigint = "") {
    update_fault_by_pk(pk_columns: { id: $id }, _set: { ended_at: $ended_at }) {
      id
      ended_at
    }
  }
`;

const GET_TEST_FAULTS = gql`
  query FaultCreator_testFaults($device_ids: [uuid!]!) {
    fault(
      where: {
        device_id: { _in: $device_ids }
        fault_comments: { comment: { _eq: "fault_creator_test" } }
        deleted_at: { _is_null: true }
      }
    ) {
      id
      code
      ended_at
    }
  }
`;

function ResolveFaultForm({ fault }) {
  const [ended, setEnded] = useState(fault.ended_at);
  const [endFault] = useMutation(END_FAULT);

  const openFaultPage = () => {
    window.open(`/fault/${fault.id}`, '_blank', 'noopener,noreferrer');
  };

  return (
    <div key={fault.id} style={{ display: 'flex', alignItems: 'center' }}>
      <Formik
        initialValues={{
          id: fault.id,
          ended_at: fault.ended_at,
          minutesAgo: 0,
        }}
        onSubmit={async (values) => {
          const { data, error } = await endFault({
            variables: {
              id: values.id,
              ended_at: Math.floor(Date.now() / 1000) - values.minutesAgo * 60,
            },
          });
          if (!error && data) {
            setEnded(data?.update_fault_by_pk?.ended_at);
          }
        }}
      >
        {({ handleSubmit, values }) => {
          if (ended) {
            return (
              <>
                <span>
                  Fault {fault.id}
                  <CopyButton data={fault.id} />
                  (code {fault.code})
                </span>
                <span style={{ display: 'inline-flex' }}>
                  <span>✅ ended at {dayjs.unix(ended).fromNow()}</span>;
                  <FeedFloButton onClick={openFaultPage}>Open Fault</FeedFloButton>
                  <FeedFloButton
                    onClick={async () => {
                      const { data, error } = await endFault({
                        variables: {
                          id: values.id,
                          ended_at: null,
                        },
                      });
                      if (!error && data) {
                        setEnded(data?.update_fault_by_pk?.ended_at);
                      }
                    }}
                  >
                    Un-end it{' '}
                  </FeedFloButton>
                </span>
              </>
            );
          }

          return (
            <>
              <span>
                Fault {fault.id}
                <CopyButton data={fault.id} />
                (code {fault.code})
              </span>
              <span style={{ display: 'inline-flex' }}>
                <NumberInputField label="End Fault X Minute Ago" placeholder="X" name="minutesAgo" value={0} />
                <FeedFloButton onClick={openFaultPage}>Open Fault</FeedFloButton>
                <FeedFloButton onClick={handleSubmit}>
                  End Fault {values?.minutesAgo !== 0 ? `${values?.minutesAgo}min ago` : 'now!'}
                </FeedFloButton>
              </span>
            </>
          );
        }}
      </Formik>
    </div>
  );
}

function CreateFaultForm() {
  const { user } = useUser();
  const [showSuccess, setShowSuccess] = useState(false);
  const now = useMemo(() => {
    return Date.now();
  }, []);
  const { data: contextData, loading: contextLoading } = useQuery(CONTEXT_GQL, {
    variables: {
      org_id_list: ['8b6f2304-a236-4ba2-ab73-db1689d0c7b5'], // FeedFlo
      before: now,
      after: now,
    },
  });
  const feedLineDeviceIds = _(contextData?.barns)
    .flatMap((b) => b.feed_lines)
    .flatMap((fl) => fl.device_assignments)
    .map((da) => da.device?.id)
    .compact()
    .value();
  const binOperatorDeviceIds = _(contextData?.barns)
    .flatMap((b) => b.bin_sets)
    .flatMap((bs) => bs.bin_controllers)
    .map((bc) => bc.device?.id)
    .compact()
    .value();
  const deviceIds = _.uniq([...feedLineDeviceIds, ...binOperatorDeviceIds]);
  const {
    data: faultsData,
    loading: faultsLoading,
    refetch: refetchFaults,
  } = useQuery(GET_TEST_FAULTS, {
    variables: {
      device_ids: deviceIds,
    },
    skip: !deviceIds || deviceIds.length === 0,
  });

  const [insertFaults] = useMutation(INSERT_FAULT_GQL);

  const [performCleanup, { data: cleanupData, loading: cleanupLoading, error: cleanupError }] = useMutation(
    CLEAN_UP_TEST_FAULTS,
    {
      variables: {
        device_ids: deviceIds,
        deleted_at: Math.floor(Date.now() / 1000),
      },
      skip: !contextData || contextLoading || !deviceIds,
    },
  );

  useEffect(() => {
    let timerId = null;

    if (showSuccess) {
      setTimeout(() => {
        setShowSuccess(false);
        // switch back to button after 3.5s
      }, 3500);
    }

    // cleanup timer
    return () => clearTimeout(timerId);
  }, [showSuccess]);

  const rootCauses = [
    { id: 'none', name: 'None' },
    ...(contextData?.fault_root_cause?.map((rc) => ({
      id: rc.id,
      name: rc?.name,
    })) || []),
  ];

  if (contextLoading || !user?.id || !contextData) {
    return <p>Loading...</p>;
  }
  return (
    <div>
      {contextLoading && 'Loading...'}
      <FeedFloButton
        onClick={() => {
          performCleanup();
          refetchFaults();
        }}
      >
        Cleanup Test Fault 🧹
      </FeedFloButton>
      {cleanupLoading && <p>Cleaning ...🧹</p>}
      {cleanupError && <p>Error: {JSON.stringify(cleanupError, null, 2)}</p>}
      {cleanupData && <p> Cleaned these Faults: {JSON.stringify(cleanupData, null, 2)}</p>}

      <Formik
        initialValues={{
          userId: user?.id,
          barnId: '',
          feedlineIds: [],
          feedLineDetails: [],
          binOperatorIds: [],
          binOperatorDetails: [],
        }}
        onSubmit={async (values) => {
          console.log(`Submit ${JSON.stringify(values, null, 2)}`);
          const now = dayjs.tz();

          const buildgqlFaultData = (formObject, code, deviceId, algorithmVersion, details = null) => {
            if (!formObject?.enable) {
              return null;
            }
            return {
              details,
              code: code,
              started_at: now.subtract(formObject.startedAtMinutes, 'minutes').unix(),
              device_id: deviceId,
              fault_comments: {
                data: [
                  {
                    comment: TEST_FAULT_TAG,
                    ...(formObject.rootCause !== 'none' ? { fault_root_cause_id: formObject.rootCause } : {}),
                  },
                ],
              },
              fault_nofeed_times: {
                data: formObject.noFeedEnable
                  ? [
                    {
                      nofeed_time_in_seconds: formObject.noFeedTimeMinutes * 60,
                      algorithm_version: algorithmVersion,
                      period_in_seconds: 259200, // C&P'd from DB
                      fill_factor: 0.6, // C&P'd from DB
                      duration_in_seconds: 1,
                    },
                  ]
                  : [],
              },
              fault_messages: {
                data: formObject.msgEnable
                  ? ['sms', 'email', 'push'].map((transport) => {
                    return {
                      reason: 'new',
                      message: {
                        data: {
                          sent_at: now.subtract(formObject.msgSentAtMinutes, 'minutes').unix(),
                          user_id: user.id,
                          sent_to: '',
                          transported_as: transport,
                        },
                      },
                    };
                  })
                  : [],
              },
            };
          };

          const submitBarn = contextData?.barns.find((b) => b.id === values.barnId);
          const submitBinControllers =
            submitBarn?.bin_sets?.flatMap((binSet) => binSet.bin_controllers)?.filter((bc) => bc.device?.id) || [];

          const feedLineFaults = values.feedLineDetails
            .map((fl) => [
              buildgqlFaultData(fl.empty, 2001, fl.deviceId, fl.algorithmVersion),
              buildgqlFaultData(fl.inactive, 2004, fl.deviceId, fl.algorithmVersion),
              buildgqlFaultData(fl.augerRunning, 2002, fl.deviceId, fl.algorithmVersion),
              buildgqlFaultData(fl.deviceOffline, 6500, fl.deviceId, fl.algorithmVersion),
            ])
            .flat(1);

          const binOperatorFaults = values.binOperatorDetails
            .map((bo) => {
              const binController = submitBinControllers.find((bc) => bc.device.id === bo.deviceId);
              return [
                buildgqlFaultData(bo.hardLockdown, 2106, bo.deviceId),
                buildgqlFaultData(
                  bo.binSwitch,
                  2101,
                  bo.deviceId,
                  null,
                  buildBinSwitchDetails(bo.binSwitch, binController),
                ),
              ];
            })
            .flat(1);

          const faultsToInsert = [...feedLineFaults, ...binOperatorFaults].filter((i) => i);
          console.log(faultsToInsert);
          await insertFaults({
            variables: {
              faults: faultsToInsert,
            },
          });
          setShowSuccess(true);
          refetchFaults();
        }}
      >
        {({ handleSubmit, values, setFieldValue, isSubmitting }) => {
          const currBarn = contextData?.barns.find((b) => b.id === values.barnId);
          const binControllers =
            currBarn?.bin_sets?.flatMap((binSet) => binSet.bin_controllers)?.filter((bc) => bc.device?.id) || [];

          useEffect(() => {
            if (!currBarn) {
              return;
            }

            const currentItems = values.feedLineDetails.map((d) => d.flId);
            const newItems = values.feedlineIds.filter((item) => !currentItems.includes(item));
            const removedItems = currentItems.filter((item) => !values.feedlineIds.includes(item));

            if (newItems.length || removedItems.length) {
              const updated = [
                ...values.feedLineDetails.filter((d) => !removedItems.includes(d.flId)),
                ...newItems.map((flId) => {
                  const currFeedLine = currBarn.feed_lines.find((fl) => fl.id === flId);
                  const device = currFeedLine.device_assignments[0]?.device;
                  return {
                    flId,
                    deviceId: device?.id,
                    algorithmVersion: device?.algorithm_version,
                    empty: {
                      enable: false,
                      startedAtMinutes: 60,
                      msgSentAtMinutes: 30,
                      msgEnable: false,
                      rootCause: 'none',
                      noFeedEnable: false,
                      noFeedTimeMinutes: 0,
                    },
                    inactive: {
                      enable: false,
                      startedAtMinutes: 60,
                      msgSentAtMinutes: 30,
                      msgEnable: false,
                      rootCause: 'none',
                      noFeedEnable: false,
                      noFeedTimeMinutes: 0,
                    },
                    augerRunning: createSimpleFaultConfig(180),
                    deviceOffline: createSimpleFaultConfig(20),
                  };
                }),
              ];
              setFieldValue('feedLineDetails', updated);
            }
          }, [values.feedlineIds, currBarn?.id]);

          useEffect(() => {
            if (!currBarn) {
              return;
            }

            const currentItems = values.binOperatorDetails.map((d) => d.deviceId);
            const newItems = values.binOperatorIds.filter((item) => !currentItems.includes(item));
            const removedItems = currentItems.filter((item) => !values.binOperatorIds.includes(item));

            if (newItems.length || removedItems.length) {
              const updated = [
                ...values.binOperatorDetails.filter((d) => !removedItems.includes(d.deviceId)),
                ...newItems.map((deviceId) => {
                  const binController = binControllers.find((bc) => bc.device.id === deviceId);
                  const defaultBin = getBinControllerBinOptions(binController)[0];
                  return {
                    deviceId,
                    hardLockdown: createSimpleFaultConfig(15),
                    binSwitch: {
                      ...createSimpleFaultConfig(1),
                      switchedToBinId: defaultBin?.id ?? '',
                    },
                  };
                }),
              ];
              setFieldValue('binOperatorDetails', updated);
            }
          }, [values.binOperatorIds, currBarn?.id]);

          return (
            <>
              <Form className="CreateFault">
                <ComboboxField
                  label="Barn"
                  name="barnId"
                  placeholder="Select a barn"
                  items={
                    contextData?.barns?.map((b) => ({
                      id: b.id,
                      name: b.name,
                    })) || []
                  }
                />
                {currBarn && (
                  <ComboboxMultiField
                    name="feedlineIds"
                    label="Flo Sensors"
                    // Filter grab devices for barn
                    items={
                      currBarn?.feed_lines.map((fl) => {
                        const device = fl.device_assignments[0]?.device;
                        return {
                          id: fl.id,
                          name: `${fl.name} - ${device?.serial}`,
                        };
                      }) || []
                    }
                  />
                )}
                {currBarn && binControllers.length > 0 && (
                  <ComboboxMultiField
                    name="binOperatorIds"
                    label="Bin Operators"
                    items={binControllers.map((bc) => ({
                      id: bc.device.id,
                      name: bc.device.serial,
                    }))}
                  />
                )}
                <FieldArray name="feedLineDetails">
                  {() => (
                    <div>
                      <h3>Fault Settings for FeedLines:</h3>
                      {values.feedLineDetails?.map((item, index) => {
                        const flId = item.flId;
                        const currFeedLine = currBarn?.feed_lines.find((fl) => fl.id === flId);
                        if (!currFeedLine) {
                          return null;
                        }
                        return (
                          <div key={currFeedLine.id} className="FeedLine">
                            <h3>{currFeedLine.name} : </h3>
                            <div className="FeedLine-Fault">
                              <h4>Empty Pipe</h4>
                              <div className="FeedLine-Fault--inputholder">
                                <LabelledColumn heading="Create?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.empty.enable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Fault Started X Minutes Ago'}
                                  name={`feedLineDetails.${index}.empty.startedAtMinutes`}
                                />
                                <LabelledColumn heading="Create Msg?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.empty.msgEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Previous message sent X min ago'}
                                  name={`feedLineDetails.${index}.empty.msgSentAtMinutes`}
                                />
                                <ComboboxField
                                  label="(optional) Add Root Cause to fault"
                                  name={`feedLineDetails.${index}.empty.rootCause`}
                                  placeholder="Select a root cause"
                                  items={rootCauses || []}
                                />
                                <LabelledColumn heading="Create NoFeed?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.empty.noFeedEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label="NoFeed Time in Minutes for Fault"
                                  name={`feedLineDetails.${index}.empty.noFeedTimeMinutes`}
                                  value={0}
                                />
                              </div>
                              <h4>Inactive Auger</h4>
                              <div className="FeedLine-Fault--inputholder">
                                <LabelledColumn heading="Create?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.inactive.enable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Fault Started X Minutes Ago'}
                                  name={`feedLineDetails.${index}.inactive.startedAtMinutes`}
                                />
                                <LabelledColumn heading="Create Msg?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.inactive.msgEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Previous message sent X min ago'}
                                  name={`feedLineDetails.${index}.inactive.msgSentAtMinutes`}
                                />
                                <ComboboxField
                                  label="(optional) Add Root Cause to Fault"
                                  name={`feedLineDetails.${index}.inactive.rootCause`}
                                  placeholder="Select a root cause"
                                  items={rootCauses || []}
                                />
                                <LabelledColumn heading="Create NoFeed?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.inactive.noFeedEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label="NoFeed Time in Minutes for Fault"
                                  name={`feedLineDetails.${index}.inactive.noFeedTimeMinutes`}
                                  value={0}
                                />
                              </div>
                              <h4>Device Offline</h4>
                              <div className="FeedLine-Fault--inputholder">
                                <LabelledColumn heading="Create?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.deviceOffline.enable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Fault Started X Minutes Ago'}
                                  name={`feedLineDetails.${index}.deviceOffline.startedAtMinutes`}
                                />
                                <LabelledColumn heading="Create Msg?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.deviceOffline.msgEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Previous message sent X min ago'}
                                  name={`feedLineDetails.${index}.deviceOffline.msgSentAtMinutes`}
                                />
                                <ComboboxField
                                  label="(optional) Add Root Cause to Fault"
                                  name={`feedLineDetails.${index}.deviceOffline.rootCause`}
                                  placeholder="Select a root cause"
                                  items={rootCauses || []}
                                />
                              </div>
                              <h4>Auger Running</h4>
                              <div className="FeedLine-Fault--inputholder">
                                <LabelledColumn heading="Create?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.augerRunning.enable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Fault Started X Minutes Ago'}
                                  name={`feedLineDetails.${index}.augerRunning.startedAtMinutes`}
                                />
                                <LabelledColumn heading="Create Msg?">
                                  <ToggleSwitchField name={`feedLineDetails.${index}.augerRunning.msgEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Previous message sent X min ago'}
                                  name={`feedLineDetails.${index}.augerRunning.msgSentAtMinutes`}
                                />
                                <ComboboxField
                                  label="(optional) Add Root Cause to Fault"
                                  name={`feedLineDetails.${index}.augerRunning.rootCause`}
                                  placeholder="Select a root cause"
                                  items={rootCauses || []}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </FieldArray>

                <FieldArray name="binOperatorDetails">
                  {() => (
                    <div>
                      <h3>Fault Settings for Bin Operators:</h3>
                      {values.binOperatorDetails?.map((item, index) => {
                        const binController = binControllers.find((bc) => bc.device.id === item.deviceId);
                        if (!binController) {
                          return null;
                        }
                        const binSwitchBinOptions = getBinControllerBinOptions(binController);
                        return (
                          <div key={item.deviceId} className="FeedLine">
                            <h3>{binController.device.serial} : </h3>
                            <div className="FeedLine-Fault">
                              <h4>Hard Lockdown</h4>
                              <div className="FeedLine-Fault--inputholder">
                                <LabelledColumn heading="Create?">
                                  <ToggleSwitchField name={`binOperatorDetails.${index}.hardLockdown.enable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Fault Started X Minutes Ago'}
                                  name={`binOperatorDetails.${index}.hardLockdown.startedAtMinutes`}
                                />
                                <LabelledColumn heading="Create Msg?">
                                  <ToggleSwitchField name={`binOperatorDetails.${index}.hardLockdown.msgEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Previous message sent X min ago'}
                                  name={`binOperatorDetails.${index}.hardLockdown.msgSentAtMinutes`}
                                />
                                <ComboboxField
                                  label="(optional) Add Root Cause to Fault"
                                  name={`binOperatorDetails.${index}.hardLockdown.rootCause`}
                                  placeholder="Select a root cause"
                                  items={rootCauses || []}
                                />
                              </div>
                              <h4>Bin Switch</h4>
                              <div className="FeedLine-Fault--inputholder">
                                <LabelledColumn heading="Create?">
                                  <ToggleSwitchField name={`binOperatorDetails.${index}.binSwitch.enable`} />
                                </LabelledColumn>
                                <ComboboxField
                                  label="Switched to Bin"
                                  name={`binOperatorDetails.${index}.binSwitch.switchedToBinId`}
                                  placeholder="Select a bin"
                                  items={binSwitchBinOptions}
                                />
                                <NumberInputField
                                  label={'Fault Started X Minutes Ago'}
                                  name={`binOperatorDetails.${index}.binSwitch.startedAtMinutes`}
                                />
                                <LabelledColumn heading="Create Msg?">
                                  <ToggleSwitchField name={`binOperatorDetails.${index}.binSwitch.msgEnable`} />
                                </LabelledColumn>
                                <NumberInputField
                                  label={'Previous message sent X min ago'}
                                  name={`binOperatorDetails.${index}.binSwitch.msgSentAtMinutes`}
                                />
                                <ComboboxField
                                  label="(optional) Add Root Cause to Fault"
                                  name={`binOperatorDetails.${index}.binSwitch.rootCause`}
                                  placeholder="Select a root cause"
                                  items={rootCauses || []}
                                />
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </FieldArray>

                <FeedFloButton
                  onClick={() => {
                    setShowSuccess(false);
                    handleSubmit();
                  }}
                >
                  Submit
                </FeedFloButton>
                <span>{isSubmitting && 'Submitting...'}</span>
                {showSuccess && 'Submitted ✅'}
              </Form>
            </>
          );
        }}
      </Formik>
      <br />
      <hr />
      <div className="FaultList">
        <h4>Faults {faultsLoading ? 'Loading ⌛️' : 'Loaded ✅'}</h4>
        <div className="Faults" style={{ display: 'flex', flexDirection: 'column', rowGap: '3rem' }}>
          {faultsData?.fault?.map((fault) => (
            <ResolveFaultForm fault={fault} key={fault.id} />
          ))}
        </div>
      </div>
    </div>
  );
}
// END FORM STUFF

function FaultCreatorPage({ titleSegments = [] }) {
  const pageTitleSegments = useMemo(() => ['Fault Creator', ...titleSegments], []);

  return (
    <Page className="FaultCreatorPage" heading="Fault Creator" titleSegments={pageTitleSegments}>
      <CreateFaultForm />
    </Page>
  );
}

FaultCreatorPage.propTypes = {
  titleSegments: PropTypes.arrayOf(PropTypes.string),
};

ResolveFaultForm.propTypes = {
  fault: PropTypes.object, // TODO: Better details maybe?
};

export default FaultCreatorPage;
